import { and, eq } from "drizzle-orm";
import type { SQLiteColumn, SQLiteTable } from "drizzle-orm/sqlite-core";
import type { PowerSyncDrizzleDB } from "@comandero/powersync";
import { nowISO } from "../utils/date-helpers";

/**
 * ── Local-first writes that must NOT be lost silently ───────────────────────
 *
 * A Drizzle `UPDATE … WHERE id = ?` against the PowerSync **view** can match
 * **ZERO rows and return “success”**. It is not a hypothesis: it is measured in
 * `space-academy-app` (same canon, the `activity` table of the local stack, course
 * `06a131d0`) — the course is created, 1.03 s later the unit is created, and **in
 * between NO `course/updated` op exists**: the title the author had just
 * written never produced a CRUD operation.
 *
 * The mechanism: when the CRUD queue drains, PowerSync **rebuilds the local
 * view from the last checkpoint**. If the checkpoint does not yet carry the
 * just-created row, the row is **ABSENT at that instant** — and an UPDATE
 * against it writes nothing, generates no op, and **does not fail**. The window
 * is not “X ms passed”: it is “the queue drained in the middle”. It cannot be
 * detected afterwards by looking at timings, and a case that landed well proves
 * nothing.
 *
 * Why the absence of error proves nothing here: the server write-path
 * returns **2xx on purpose** (a 4xx would block the client queue
 * forever). On a POS, a user write that vanishes without a trace
 * is the worst possible failure mode: an order, an order line, or a
 * table bill the waiter believes was saved.
 *
 * Contract of `patchRowVerified`: write, **RE-READ**, compare. If the
 * value did not land, retry with backoff; if after exhausting it still has not
 * landed, **THROW** — the caller reports it instead of leaving the user
 * looking at a field that represents nothing saved.
 *
 * Unlike the canon (`listto-app` 9ae347b6) and `folio-office-app`
 * (1a7b5b3), `waitForRowPresent` is NOT carried here: that primitive covers an
 * `UPDATE` that lives inside a `db.transaction(...)` (it cannot be retried
 * there, it would sleep holding the write lock) where a sibling INSERT of the
 * SAME transaction does happen no matter what. No mutation in this repo
 * opens a `db.transaction(...)`: the only `writeTransaction` there is
 * (`p2p/snapshot.ts`) is device-to-device transfer import/export, not a
 * domain write. A signal that would reopen this: a mutation that starts
 * wrapping an INSERT+UPDATE in a transaction.
 *
 * ── What this module does NOT cover (deliberate) ────────────────────────────
 * Hard `DELETE`s (`purgeTrashItemMutation`, `deleteReservationMutation`,
 * the catalog link cascades) are not verified: their natural check would be
 * “the row is gone”, and “absent” is exactly the ambiguous state of
 * this window — you cannot tell “it was deleted” from “it had not arrived yet”.
 * Verifying them would require another signal (the CRUD queue itself), not a re-read.
 * A signal that would reopen it: a delete that is really lost (row that
 * reappears after being deleted).
 *
 * `user` DOES go through here (`users/mutations.ts`) — and STILL goes through
 * here even though the root cause that uncovered it is already closed:
 *
 *   - Until 2026-08-13, `ThemeProvider.createEffect`
 *     (`apps/web/src/providers/theme-provider.tsx`) inserted the `user` row
 *     LOCALLY for any `uid` (not only the guest), without waiting for
 *     `hasSynced`. For a real account that local seeding raced the row
 *     the server ALREADY creates unconditionally on signup — two INSERTs
 *     for the same id during the same checkpoint window. Measured in the
 *     `infra` audit `d87476c` (11/11 repos with the same false criterion,
 *     canon included). The fix (`uid !== VISITOR_ID`, same pattern as
 *     `i18n-provider.tsx`/`typography-provider.tsx`) closed THAT mechanism: the
 *     local seed is now guest-only; a real account never re-inserts its own
 *     `user` row.
 *   - But this module's generic window (see the `course` example at the
 *     top of the file) never depended on that seeding: `userId()` is
 *     triggered by a path (URL `/u/<n>` → account registry, or `get-session`
 *     REST) independent of the sync stream, so a freshly
 *     created account can touch its first preference BEFORE the
 *     checkpoint brings its own `user` row — with or without local seeding in
 *     between. `patchRowVerified` on `user` is not a leftover of the already
 *     closed defect: it is DEFENSE IN DEPTH against that generic window, the same
 *     one that protects `order`/`customer`/`ingredient`/etc. If it is ever
 *     removed, it must be because that generic window stopped existing for
 *     `user` — not because local seeding no longer races.
 *
 * No mutation in this repo touches more than one row per write (unlike
 * `folio-office-app`, which moves a page and its cohort of
 * subpages at once): a batch variant is not needed.
 *
 * ── `scope`: tenant isolation, not just correctness ─────────────────────────
 * `recomputeOrderTotals`/`applyOrderDiscountMutation`/`closeRegisterSessionMutation`
 * used to filter by `and(eq(id), eq(organization_id))`. A first draft of this
 * fix assumed `organization_id` was redundant because `id` (client UUID) already
 * is the primary key — CORRECT for correctness, but the condition was not there
 * only for that: `infra/sync_streams.yaml` syncs `"order"`/`register_session`
 * with `WHERE organization_id IN my_org_ids`, where `my_org_ids` is ALL the
 * organizations the user is `member` of — not only the active one (the
 * YAML's own comment says it: "the client filters by active branch_id").
 * An employee who belongs to two locations has BOTH synced at once in
 * SQLite. `orderId` also travels in the URL (`?order=`, addressable ticket) —
 * external input. Without the `organization_id` filter, an `UPDATE … WHERE id = ?`
 * launched from organization A's context would land just the same on a
 * row of organization B if the `id` matches. `scope` restores that filter
 * (applied to the UPDATE and to the re-read) without resurrecting the raw `and()`: if the
 * row does not meet the scope, the re-read does not see it, the write never
 * confirms and `patchRowVerified` ends up throwing — same as “absent row”,
 * but for the right reason: it is not yours.
 */

/**
 * Retry backoff. Sum ≈ 6.05 s: comfortably covers a checkpoint round-trip
 * without leaving a quick interaction —changing a table's state, marking a
 * line as served— hanging forever if the row truly does not exist.
 */
export const VERIFIED_WRITE_BACKOFF_MS = [50, 100, 200, 400, 800, 1500, 3000] as const;

const sleep = (ms: number): Promise<void> =>
  new Promise((resolve) => {
    setTimeout(resolve, ms);
  });

/** Additional equality condition — in-depth tenant isolation. */
export interface ScopeCondition {
  column: SQLiteColumn;
  value: unknown;
}

export interface VerifiedWriteOptions {
  /** Waits between retries. Exposed so tests do not take seconds. */
  backoffMs?: readonly number[];
  /**
   * Extra conditions the row must ALSO meet, besides `id` —
   * typically `organization_id`. Applied to both the UPDATE and the
   * re-read: a row that does not meet the scope is treated like an absent
   * row (see the security note above).
   */
  scope?: readonly ScopeCondition[];
}

/** Error of a local-first write that never landed in the view. */
export class LocalWriteLostError extends Error {
  constructor(
    readonly rowId: string,
    readonly fields: readonly string[],
  ) {
    super(
      `La escritura local no aterrizó en la fila ${rowId} (campos: ${fields.join(", ")}). ` +
        `La fila no está en la vista de PowerSync o el valor no se guardó.`,
    );
    this.name = "LocalWriteLostError";
  }
}

/** Builds `eq(id) AND eq(scope1) AND …` — or just `eq(id)` with no scope. */
function idAndScopeCondition(idColumn: SQLiteColumn, id: string, scope: readonly ScopeCondition[]) {
  if (scope.length === 0) {
    return eq(idColumn, id);
  }
  return and(eq(idColumn, id), ...scope.map((s) => eq(s.column, s.value)));
}

/**
 * Reads the row by id (+ `scope`, if given), or `undefined` if it is not in the
 * local view or does not meet the scope — same result as "absent row", on
 * purpose (see the security note above).
 */
async function readRow(
  db: PowerSyncDrizzleDB,
  table: SQLiteTable,
  idColumn: SQLiteColumn,
  id: string,
  scope: readonly ScopeCondition[] = [],
): Promise<Record<string, unknown> | undefined> {
  // Drizzle cannot infer the shape of a generic `SQLiteTable`; the read is by
  // primary key (+ scope), so the row is a flat record of columns.
  // oxlint-disable-next-line typescript/no-unsafe-type-assertion
  const rows = (await db
    .select()
    .from(table)
    .where(idAndScopeCondition(idColumn, id, scope))
    .limit(1)) as unknown as Record<string, unknown>[];
  return rows[0];
}

/** `null` and `undefined` are the same “empty” when comparing against SQLite. */
const sameValue = (a: unknown, b: unknown): boolean => (a ?? null) === (b ?? null);

/**
 * **Verified** `UPDATE … WHERE id = ?`: write, re-read, compare the patch
 * fields, and retry while they have not landed. Throws `LocalWriteLostError`
 * if after exhausting the backoff they still have not.
 *
 * `updated_at` is set by this function on every attempt (and not compared:
 * it changes on each pass); `patch` must carry only the domain fields.
 *
 * Only for use OUTSIDE a `db.transaction(...)`: it retries with `sleep`, which
 * inside an open transaction would sleep holding the write lock.
 */
export async function patchRowVerified(
  db: PowerSyncDrizzleDB,
  table: SQLiteTable,
  idColumn: SQLiteColumn,
  id: string,
  patch: Record<string, unknown>,
  options: VerifiedWriteOptions = {},
): Promise<void> {
  const backoffMs = options.backoffMs ?? VERIFIED_WRITE_BACKOFF_MS;
  const scope = options.scope ?? [];

  for (let attempt = 0; ; attempt++) {
    await db
      .update(table)
      .set({ ...patch, updated_at: nowISO() })
      .where(idAndScopeCondition(idColumn, id, scope));

    const row = await readRow(db, table, idColumn, id, scope);
    if (row && Object.entries(patch).every(([key, value]) => sameValue(row[key], value))) {
      return;
    }

    const wait = backoffMs[attempt];
    if (wait === undefined) {
      throw new LocalWriteLostError(id, Object.keys(patch));
    }
    await sleep(wait);
  }
}
